import { feathers } from '@feathersjs/feathers';
import rest from '@feathersjs/rest-client';
import socketio from '@feathersjs/socketio-client';
import auth from '@feathersjs/authentication-client';
import axios from 'axios';
import io from 'socket.io-client';
import { toast } from '@/hooks/use-toast';  // Import the toast function
import {
  clearConnectivityIncident,
  recordNewRelicPageAction,
  updateConnectivityIncident,
} from '@/lib/newrelic-browser';
import { resolveApiUrl } from '@/lib/resolveApiUrl';

const API_URL = resolveApiUrl();

type FeathersValidationError = {
  instancePath?: string;
  message?: string;
};

type FeathersStructuredErrorData = {
  userMessage?: string;
  message?: string;
  name?: string;
};

export type FeathersErrorLike = {
  message?: string;
  name?: string;
  data?: FeathersValidationError[] | FeathersStructuredErrorData;
};

export const skipErrorToastParams = {
  headers: { 'X-Skip-Error-Toast': '1' },
} as const;

export const getFeathersErrorMessage = (
  error: unknown,
  fallback = 'An unexpected error occurred'
): string => {
  if (!error || typeof error !== 'object') {
    return fallback;
  }

  const feathersError = error as FeathersErrorLike;

  if (
    feathersError.data &&
    typeof feathersError.data === 'object' &&
    !Array.isArray(feathersError.data)
  ) {
    const structuredData = feathersError.data as FeathersStructuredErrorData;
    if (structuredData.userMessage?.trim()) {
      return structuredData.userMessage;
    }
  }

  if (Array.isArray(feathersError.data) && feathersError.data.length > 0) {
    const validationErrors = feathersError.data
      .map((validationError) => {
        const field = validationError.instancePath
          ? validationError.instancePath.replace(/^\//, '')
          : 'Field';
        return `${field || 'Field'}: ${validationError.message}`;
      })
      .join(', ');

    return feathersError.message
      ? `${feathersError.message}. ${validationErrors}`
      : validationErrors;
  }

  return feathersError.message || fallback;
};

const getRequestPath = (url?: string) => {
  if (!url) return undefined;

  try {
    const parsedUrl = new URL(url, API_URL || window.location.origin);
    return parsedUrl.pathname;
  } catch {
    return url.split('?')[0];
  }
};

const trackConnectivityIssue = (
  source: string,
  attributes: Record<string, string | number | boolean | undefined> = {},
  throttleMs?: number
) => {
  const incident = updateConnectivityIncident((currentIncident) => ({
    ...currentIncident,
    failureCount: currentIncident.failureCount + 1,
  }));

  recordNewRelicPageAction(
    'frontend_connectivity_issue',
    {
      incidentId: incident.id,
      incidentPhase: incident.failureCount === 1 ? 'started' : 'retrying',
      source,
      failureCount: incident.failureCount,
      ...attributes,
    },
    `${incident.id}:${source}:${incident.failureCount === 1 ? 'started' : 'retrying'}`,
    throttleMs
  );

  return incident;
};

const trackConnectivityRecovered = (source: string, attributes: Record<string, string | number | boolean | undefined> = {}) => {
  const incident = clearConnectivityIncident();

  if (!incident) {
    return;
  }

  recordNewRelicPageAction(
    'frontend_connectivity_recovered',
    {
      incidentId: incident.id,
      incidentPhase: 'recovered',
      source,
      outageDurationMs: Date.now() - incident.startedAt,
      failureCount: incident.failureCount,
      reconnectAttempts: incident.reconnectAttempts,
      ...attributes,
    },
    `${incident.id}:recovered`,
    0
  );
};

const trackReconnectAttempt = (source: string, attributes: Record<string, string | number | boolean | undefined> = {}) => {
  const incident = updateConnectivityIncident((currentIncident) => ({
    ...currentIncident,
    reconnectAttempts: currentIncident.reconnectAttempts + 1,
  }));

  recordNewRelicPageAction(
    'frontend_reconnect_attempt',
    {
      incidentId: incident.id,
      source,
      reconnectAttempts: incident.reconnectAttempts,
      failureCount: incident.failureCount,
      ...attributes,
    },
    `${incident.id}:reconnect_attempt`,
    5000
  );
};

const trackReconnectFailed = (source: string, attributes: Record<string, string | number | boolean | undefined> = {}) => {
  const incident = clearConnectivityIncident();

  recordNewRelicPageAction(
    'frontend_reconnect_failed',
    {
      incidentId: incident?.id,
      incidentPhase: 'failed',
      source,
      outageDurationMs: incident ? Date.now() - incident.startedAt : undefined,
      failureCount: incident?.failureCount,
      reconnectAttempts: incident?.reconnectAttempts,
      ...attributes,
    },
    incident ? `${incident.id}:failed` : 'frontend_reconnect_failed',
    0
  );
};

const clearAuthAndRefresh = () => {
  if (typeof window === 'undefined') return;
  
  console.log('[Login:Error] Clearing authentication data and refreshing page');
  
  localStorage.clear();
  
  document.cookie.split(";").forEach((c) => {
    const eqPos = c.indexOf("=");
    const name = eqPos > -1 ? c.substr(0, eqPos) : c;
    document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
    document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=" + window.location.hostname;
    document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=." + window.location.hostname;
  });
  
  window.location.reload();
};

const hardReload = () => {
  if (typeof window === 'undefined') return;
  
  console.log('[Login:Error] Performing hard reload (bypassing cache)');
  
  localStorage.clear();
  
  document.cookie.split(";").forEach((c) => {
    const eqPos = c.indexOf("=");
    const name = eqPos > -1 ? c.substr(0, eqPos) : c;
    document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
    document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=" + window.location.hostname;
    document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=." + window.location.hostname;
  });
  
  // Hard reload: replace current location to force fresh fetch from server
  window.location.replace(window.location.href);
};

console.log('Initializing Feathers clients with API_URL:', API_URL);

// Create separate apps for REST and Socket.IO
const restApp = feathers();
const socketApp = feathers();

// Initialize the REST client
const restClient = rest(API_URL);

// Configure the REST client with Axios
if (typeof window !== 'undefined') {
  // Client-side configuration
  const axiosInstance = axios.create({
    headers: { 'X-Requested-With': 'AchieveApex-Frontend' }
  });

  // Add an error interceptor
  axiosInstance.interceptors.response.use(
    response => response,
    error => {
      const statusCode = error.response?.status;
      const isAuthEndpoint = error.config?.url?.includes('/authentication');
      
      if (isAuthEndpoint && statusCode === 500) {
        recordNewRelicPageAction('frontend_auth_http_500', {
          requestPath: getRequestPath(error.config?.url),
          requestMethod: error.config?.method?.toUpperCase(),
        });
        console.log('[Login:Error] Server error (500) during authentication, performing hard reload', {
          statusCode,
          url: error.config?.url
        });
        hardReload();
        return Promise.reject(error);
      }
      
      if (isAuthEndpoint && statusCode === 503) {
        recordNewRelicPageAction('frontend_auth_http_503', {
          requestPath: getRequestPath(error.config?.url),
          requestMethod: error.config?.method?.toUpperCase(),
        });
        console.log('[Login:Error] Server error (503) during authentication, clearing auth and refreshing', {
          statusCode,
          url: error.config?.url
        });
        clearAuthAndRefresh();
        return Promise.reject(error);
      }
      
      // Check if this is a network/connection error
      const isNetworkError = 
        !error.response && (
          error.code === 'ERR_NETWORK' ||
          error.code === 'ECONNREFUSED' ||
          error.message?.includes('Network Error') ||
          error.message?.includes('ERR_CONNECTION_REFUSED')
        );

      // Don't show toasts for network errors during server disconnection/reconnection
      if (isNetworkError && isServerDisconnected) {
        trackConnectivityIssue(
          'rest_disconnected',
          {
            requestPath: getRequestPath(error.config?.url),
            requestMethod: error.config?.method?.toUpperCase(),
            errorCode: error.code,
          },
          30000
        );
        console.log('[REST:Client] Network error during server disconnection - silently ignoring', {
          url: error.config?.url,
          method: error.config?.method
        });
        return Promise.reject(error);
      }

      let title = "Error";
      let description = "An unexpected error occurred";

      if (error.response && error.response.data) {
        const errorData = error.response.data;

        title = errorData.name || "Error";
        description = getFeathersErrorMessage(errorData, description);
      } else if (isNetworkError) {
        trackConnectivityIssue('rest_network', {
          requestPath: getRequestPath(error.config?.url),
          requestMethod: error.config?.method?.toUpperCase(),
          errorCode: error.code,
        });
        // Network error but server might be back - show informative message
        title = "Connection Error";
        description = "Unable to reach server. Please check your connection.";
      }

      // Only show toast for non-network errors or network errors when server is connected
      const skipErrorToast = error.config?.headers?.['X-Skip-Error-Toast'] === '1'
      if ((!isNetworkError || !isServerDisconnected) && !skipErrorToast) {
        toast({
          title,
          description,
          variant: "destructive",
        });
      }

      return Promise.reject(error);
    }
  );

  restApp.configure(restClient.axios(axiosInstance));
} else {
  // Server-side configuration
  restApp.configure(
    restClient.axios(
      axios.create({
        headers: { 'X-Requested-With': 'AchieveApex-Backend' }
      })
    )
  );
}

// Configure authentication for REST with specific options
restApp.configure(auth({
  storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  storageKey: 'feathers-jwt', // Explicitly set the storage key
  path: '/authentication', // Explicitly set the authentication path
  jwtStrategy: 'jwt', // Specify the JWT strategy
  locationKey: 'access_token', // Specify the location key
}));

// Configure authentication for Socket with the same options
socketApp.configure(auth({
  storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  storageKey: 'feathers-jwt',
  path: '/authentication',
  jwtStrategy: 'jwt',
  locationKey: 'access_token',
}));

// Add error handlers for both clients
restApp.hooks({
  error: async (context) => {
    console.error('REST Error:', context.error);
  }
});

socketApp.hooks({
  error: async (context) => {
    console.error('Socket Error:', context.error);
  }
});

// Track if this is a reconnection to handle re-authentication properly
let isReconnecting = false;
let isInitialConnection = true;
let isServerDisconnected = false;
let disconnectToast: { dismiss: () => void } | null = null;

// Initialize and configure the Socket.io client with reconnection options
// For horizontal scaling with sticky sessions, prioritize WebSocket to avoid polling issues
const socket = io(API_URL || '', {
  transports: ['websocket', 'polling'], // Try WebSocket first, fallback to polling
  upgrade: true, // Allow upgrade from polling to WebSocket
  rememberUpgrade: true, // Remember WebSocket preference
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 2000,
  timeout: 20000,
  forceNew: false, // Reuse existing connection if available
  autoConnect: true  // Enable auto-connect for automatic reconnection
});

// Helper function to dismiss disconnect toast and show success message
const handleReconnectionSuccess = () => {
  if (disconnectToast && typeof window !== 'undefined') {
    disconnectToast.dismiss();
    disconnectToast = null;
    
    const successToast = toast({
      title: "Connection Restored",
      description: "Successfully reconnected to server",
      variant: "success",
    });
    
    // Auto-dismiss success toast after 3 seconds
    setTimeout(() => {
      successToast.dismiss();
    }, 3000);
  }
};

// Add connection event handlers
socket.on('connect', async () => {
  console.log('[SocketIO:Client] Connected to server', { 
    socketId: socket.id, 
    isReconnecting, 
    isInitialConnection 
  });
  
  // Mark server as connected again
  isServerDisconnected = false;
  
  // If this is a reconnection, dismiss the disconnect toast and show success
  if (isReconnecting) {
    trackConnectivityRecovered('socket_connect', {
      socketId: socket.id,
    });
    handleReconnectionSuccess();
  }
  
  // Re-authenticate after reconnection if we have a token stored
  // Skip on initial connection (UserContext will handle initial auth)
  if (isReconnecting && typeof window !== 'undefined') {
    try {
      const token = window.localStorage.getItem('feathers-jwt');
      if (token) {
        console.log('[SocketIO:Client] Re-authenticating after reconnection...');
        await socketApp.authenticate({
          strategy: 'jwt',
          accessToken: token,
        });
        console.log('[SocketIO:Client] ✅ Re-authentication successful');
        isReconnecting = false;
      } else {
        console.log('[SocketIO:Client] No token found, skipping re-authentication');
        isReconnecting = false;
      }
    } catch (error) {
      console.error('[SocketIO:Client] Re-authentication failed after reconnection:', error);
      // Try to re-authenticate using REST client and then socket
      try {
        const restAuth = await restFeathers.reAuthenticate();
        await socketApp.authenticate({
          strategy: 'jwt',
          accessToken: restAuth.accessToken,
        });
        console.log('[SocketIO:Client] ✅ Re-authentication via REST successful');
        isReconnecting = false;
      } catch (restError) {
        console.error('[SocketIO:Client] Re-authentication via REST also failed:', restError);
        toast({
          title: "Connection Restored",
          description: "Please refresh the page if you experience issues",
          variant: "informative",
        });
        isReconnecting = false;
      }
    }
  }
  
  // Mark that initial connection is done
  if (isInitialConnection) {
    isInitialConnection = false;
  }
});

socket.on('disconnect', (reason: string) => {
  console.log('[SocketIO:Client] Disconnected from server', { reason });
  
  // Mark as reconnecting if it's not a manual disconnect
  if (reason !== 'io client disconnect') {
    isReconnecting = true;
    isServerDisconnected = true; // Mark server as disconnected to suppress REST error toasts
    trackConnectivityIssue('socket_disconnect', { reason }, 15000);
  }
  
  // Show different messages based on disconnect reason
  if (reason === 'io server disconnect') {
    // Server forcefully disconnected (e.g., during ECS task replacement)
    disconnectToast = toast({
      title: "Reconnecting",
      description: "Server disconnected. Reconnecting automatically...",
      variant: "informative",
    });
  } else if (reason === 'transport close') {
    disconnectToast = toast({
      title: "Connection Lost",
      description: "Connection lost. Reconnecting automatically...",
      variant: "informative",
    });
  } else if (reason === 'ping timeout') {
    disconnectToast = toast({
      title: "Connection Timeout",
      description: "Connection timed out. Reconnecting...",
      variant: "informative",
    });
  }
});

socket.on('reconnect', (attemptNumber: number) => {
  console.log('[SocketIO:Client] Reconnected after', attemptNumber, 'attempts');
  trackConnectivityRecovered('socket_reconnect', { attemptNumber });
  handleReconnectionSuccess();
});

socket.on('reconnect_attempt', (attemptNumber: number) => {
  console.log('[SocketIO:Client] Reconnection attempt', attemptNumber);
  trackReconnectAttempt('socket_reconnect_attempt', { attemptNumber });
});

socket.on('reconnect_error', (error: Error) => {
  console.error('[SocketIO:Client] Reconnection error:', error);
  trackConnectivityIssue('socket_reconnect_error', { errorMessage: error.message }, 15000);
});

socket.on('reconnect_failed', () => {
  console.error('[SocketIO:Client] Reconnection failed after all attempts');
  trackReconnectFailed('socket_reconnect_failed');
  toast({
    title: "Connection Failed",
    description: "Unable to reconnect. Please refresh the page.",
    variant: "destructive",
  });
});

// Add error event handler
socket.on('error', (error: Error) => {
  console.error('Socket error:', error);
  trackConnectivityIssue('socket_error', { errorMessage: error.message }, 15000);
  toast({
    title: "Socket Error",
    description: error.message || "An unexpected error occurred",
    variant: "destructive",
  });
});

socketApp.configure(socketio(socket));

console.log('Feathers clients configured with Axios (REST) and Socket.io');

// Export the configured apps with different names
export const restFeathers = restApp;
export const socketFeathers = socketApp;
